export const CATEGORIES = [
  {
    id: 'overview',
    title: 'Visión general',
    description: 'Arquitectura, flujo base y conceptos principales de ReportEngine.',
    icon: 'file-text',
    color: '#4F46E5',
    articles: [
      {
        id: 'overview',
        title: 'Visión general de la plataforma',
        updatedAt: '21 Feb 2026',
        readTime: '5 min',
        content: `## ¿Qué es ReportEngine?
ReportEngine combina Studio + API + Workers para generar documentos en PDF, imagen y HTML a escala.

## Arquitectura
### Studio
Interfaz visual para crear plantillas, administrar assets y previsualizar reportes.

### API REST
Expone endpoints para render, templates, jobs, reports y operaciones administrativas.

### Workers
Procesan trabajos asíncronos, colas y tareas programadas.

## Flujo rápido
1. Diseña la plantilla en Studio.
2. Envía datos JSON al endpoint de render.
3. Descarga el documento generado.`
      }
    ]
  },
  {
    id: 'templates',
    title: 'Templates',
    description: 'Cómo construir plantillas Handlebars y elegir el recipe correcto.',
    icon: 'file-text',
    color: '#6366F1',
    articles: [
      {
        id: 'templates-basics',
        title: 'Plantillas y Handlebars',
        updatedAt: '21 Feb 2026',
        readTime: '7 min',
        content: `## Estructura de plantilla
Una plantilla define contenido, helpers y recipe.

### Campos principales
- \`contentHtml\`
- \`helpersJs\`
- \`engine\`
- \`recipe\`

### Ejemplo
\`\`\`html
<h1>Factura {{invoice.number}}</h1>
{{#each items}}
  <p>{{this.name}} - {{this.price}}</p>
{{/each}}
\`\`\`

## Recipes
### chrome-pdf
Salida en PDF para documentos empresariales.

### chrome-image
Salida PNG/JPEG para previews o etiquetas.

### html
Salida HTML para email o vista web.`
      }
    ]
  },
  {
    id: 'render',
    title: 'Renderizado',
    description: 'Render síncrono, render inline y opciones avanzadas de salida.',
    icon: 'zap',
    color: '#22D3EE',
    articles: [
      {
        id: 'render-sync',
        title: 'Render síncrono con /api/render',
        updatedAt: '21 Feb 2026',
        readTime: '6 min',
        content: `## Render síncrono
Usa \`POST /api/render\` cuando necesites el archivo inmediatamente.

### Payload básico
\`\`\`json
{
  "template": { "id": "tpl_abc123" },
  "data": { "invoice": { "number": "FAC-001" } }
}
\`\`\`

### Headers de respuesta
- \`Content-Type\`
- \`X-Report-Id\`
- \`X-Request-Id\`

## Render inline
Puedes enviar \`contentHtml\` sin guardar una plantilla.`
      }
    ]
  },
  {
    id: 'async',
    title: 'Render asíncrono',
    description: 'Jobs en cola, prioridad y seguimiento de estado de ejecución.',
    icon: 'refresh-cw',
    color: '#8B5CF6',
    articles: [
      {
        id: 'async-flow',
        title: 'Flujo asíncrono en 3 pasos',
        updatedAt: '21 Feb 2026',
        readTime: '8 min',
        content: `## Paso 1: Encolar
\`POST /api/render/async\` devuelve \`jobId\`.

## Paso 2: Consultar estado
\`GET /api/jobs/:jobId\` para revisar progreso.

## Paso 3: Descargar
Cuando el job termina, descarga con \`GET /api/reports/:reportId\`.

### Estados
- \`queued\`
- \`processing\`
- \`completed\`
- \`failed\`
- \`canceled\`

### Prioridad y dedupe
Configura \`priority\` y \`dedupeKey\` para mejor control de cola.`
      }
    ]
  },
  {
    id: 'scripts',
    title: 'Scripts',
    description: 'Automatización con beforeRender y afterRender dentro del sandbox.',
    icon: 'code-2',
    color: '#34D399',
    articles: [
      {
        id: 'scripts-guide',
        title: 'Scripts beforeRender y afterRender',
        updatedAt: '21 Feb 2026',
        readTime: '9 min',
        content: `## beforeRender
Transforma o valida datos antes de generar.

## afterRender
Agrega metadatos, alertas o lógica posterior al render.

### Ejemplo rápido
\`\`\`javascript
async function beforeRender(req, res) {
  req.data.total = (req.data.items || []).reduce((a, i) => a + Number(i.price || 0), 0);
}
beforeRender(req, res);
\`\`\`

### Recomendaciones
- Valida campos obligatorios.
- Evita lógica pesada.
- Usa logs para observabilidad.`
      }
    ]
  },
  {
    id: 'assets',
    title: 'Assets',
    description: 'Gestión de imágenes, fuentes y archivos reutilizables.',
    icon: 'upload',
    color: '#F59E0B',
    articles: [
      {
        id: 'assets-guide',
        title: 'Subida y uso de assets',
        updatedAt: '21 Feb 2026',
        readTime: '4 min',
        content: `## Tipos soportados
PNG, JPG, SVG, WOFF/WOFF2, CSS y más.

## Uso en plantilla
\`\`\`html
<img src="{{asset 'logo.png'}}" />
\`\`\`

## Buenas prácticas
- Nombres consistentes.
- Versionado por carpeta.
- Reutiliza assets comunes entre plantillas.`
      }
    ]
  },
  {
    id: 'jobs',
    title: 'Jobs programados',
    description: 'Automatiza reportes con cron y zonas horarias.',
    icon: 'clock',
    color: '#0EA5E9',
    articles: [
      {
        id: 'scheduled-jobs',
        title: 'Programación de jobs',
        updatedAt: '21 Feb 2026',
        readTime: '6 min',
        content: `## Cron expressions
Define frecuencia con cron y timezone.

### Ejemplos
- \`0 * * * *\` cada hora
- \`0 8 * * *\` diario 8:00
- \`0 8 * * 1\` lunes 8:00

## Monitoreo
Consulta estado, logs y cancelación desde la API.`
      }
    ]
  },
  {
    id: 'auth',
    title: 'Autenticación y seguridad',
    description: 'JWT, roles, aislamiento de datos y seguridad de ejecución.',
    icon: 'shield',
    color: '#22C55E',
    articles: [
      {
        id: 'auth-security',
        title: 'JWT, roles y seguridad',
        updatedAt: '21 Feb 2026',
        readTime: '7 min',
        content: `## JWT Bearer
Incluye \`Authorization: Bearer <token>\` en rutas protegidas.

## Roles
- admin
- editor
- viewer

## Seguridad
- expiración de token
- sandbox para scripts
- TLS en producción
- rate limiting por plan`
      }
    ]
  },
  {
    id: 'users',
    title: 'Gestión de usuarios',
    description: 'Administración de usuarios, roles y acceso por workspace.',
    icon: 'users',
    color: '#14B8A6',
    articles: [
      {
        id: 'users-management',
        title: 'Administración de usuarios',
        updatedAt: '21 Feb 2026',
        readTime: '4 min',
        content: `## Operaciones comunes
- Crear usuario
- Cambiar contraseña
- Asignar rol
- Eliminar usuario

## Endpoints
- \`GET /api/users\`
- \`POST /api/users\`
- \`PUT /api/users/:id/password\`
- \`DELETE /api/users/:id\``
      }
    ]
  },
  {
    id: 'billing',
    title: 'Billing y planes',
    description: 'Límites, features y estados de suscripción por plan.',
    icon: 'credit-card',
    color: '#F97316',
    articles: [
      {
        id: 'billing-plans',
        title: 'Planes, límites y suscripción',
        updatedAt: '21 Feb 2026',
        readTime: '5 min',
        content: `## Límites
- rendersMonth
- templatesActive
- concurrentRenders
- apiCallsPerMinute
- storageGB

## Features
- studio
- apiAccess
- priorityQueue
- customBranding

## Estado de suscripción
active, trialing, pending_payment, past_due, suspended, canceled.`
      }
    ]
  },
  {
    id: 'branding',
    title: 'Branding y personalización',
    description: 'Logo, colores, legal y traducciones desde Admin Settings.',
    icon: 'palette',
    color: '#A855F7',
    articles: [
      {
        id: 'branding-customization',
        title: 'Personalización del entorno',
        updatedAt: '21 Feb 2026',
        readTime: '5 min',
        content: `## Branding
Personaliza nombre, logo, favicon y paleta visual.

## Contenido legal
Edita términos, privacidad y aviso legal.

## Idiomas
Clona un idioma base y modifica traducciones para toda la UI.`
      }
    ]
  }
];

export const QUICK_TAGS = [
  { label: 'Render API', href: '/c/render/render-sync' },
  { label: 'Jobs Async', href: '/c/async/async-flow' },
  { label: 'Scripts', href: '/c/scripts/scripts-guide' },
  { label: 'Billing', href: '/c/billing/billing-plans' },
];

export function getCategoryById(categoryId) {
  return CATEGORIES.find((cat) => cat.id === categoryId);
}

export function getArticleById(categoryId, articleId) {
  const category = getCategoryById(categoryId);
  if (!category) return undefined;
  return category.articles.find((article) => article.id === articleId);
}
